import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  Building2,
  Mail,
  Users,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  Search,
  Filter,
  Upload,
  Download,
  Eye,
  Edit,
  Trash2,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Calendar,
  User,
  Shield,
  FileText,
  Image as ImageIcon,
  Settings,
  Database,
  Globe,
  Lock,
  Unlock,
  Star,
  Award,
  Briefcase,
  Phone,
  MapPin,
  Link,
  Clock,
  Save,
  Copy,
  ExternalLink,
  MoreHorizontal,
  ChevronRight,
  Info,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  MessageCircle,
  BookOpen,
  TrendingUp,
  MessageSquare,
  Activity,
} from 'lucide-react';
import {
  getUsersWithFilters,
  updateUserBusinessStage,
  uploadUserImage,
  getAuditLogs,
} from '../../database/firebase';
import {
  collection,
  query as firestoreQuery,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { BUSINESS_STAGES } from '../../constants/businessStages';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const ClientStageUpdater = () => {
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('email');
  const [selectedCollections, setSelectedCollections] = useState(new Set());
  const [filters, setFilters] = useState({
    role: '',
    status: '',
    businessStage: '',
    collection: '',
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [selectedItemForAudit, setSelectedItemForAudit] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [newStage, setNewStage] = useState('');
  const [updateReason, setUpdateReason] = useState('');
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [imageUploadType, setImageUploadType] = useState('profile');
  const [imageUploadItem, setImageUploadItem] = useState(null);

  const [viewMode, setViewMode] = useState('grid');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [collectionStats, setCollectionStats] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, item: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const collections = [
    {
      name: 'admin',
      icon: Shield,
      color: 'red',
      description: 'Administrator accounts',
      fields: ['email', 'firstname', 'lastname', 'role'],
    },
    {
      name: 'alumni',
      icon: Award,
      color: 'purple',
      description: 'Alumni members',
      fields: [
        'email',
        'firstname',
        'lastname',
        'organization',
        'phone',
        'program',
        'role',
        'status',
        'businessStage',
      ],
    },
    {
      name: 'alumniReports',
      icon: FileText,
      color: 'blue',
      description: 'Alumni reports and submissions',
      fields: [
        'email',
        'title',
        'content',
        'status',
        'attachments',
        'fileData',
        'uploadedBy',
        'createdAt',
        'userType',
      ],
    },
    {
      name: 'bif_applications',
      icon: Briefcase,
      color: 'green',
      description: 'Business Innovation Fund applications',
      fields: ['company', 'status', 'createdAt', 'updatedAt', 'userId', 'userEmail', 'files'],
    },
    {
      name: 'clients',
      icon: Users,
      color: 'indigo',
      description: 'Client profiles and data',
      fields: [
        'uid',
        'email',
        'firstname',
        'lastname',
        'role',
        'status',
        'organization',
        'location',
        'businessStage',
        'stage',
        'cohortGroup',
        'profilePicture',
        'bannerPhoto',
        'organizationLogo',
        'createdAt',
        'updatedAt',
      ],
    },
    {
      name: 'coaches',
      icon: User,
      color: 'orange',
      description: 'Coach profiles',
      fields: [
        'email',
        'firstname',
        'lastname',
        'role',
        'status',
        'organization',
        'specialization',
      ],
    },
    {
      name: 'companyClient',
      icon: Building2,
      color: 'teal',
      description: 'Company client profiles',
      fields: [
        'organization',
        'address',
        'phone',
        'website',
        'kycDocuments',
        'kycStatus',
        'status',
        'organizationId',
        'updatedAt',
      ],
    },
    {
      name: 'companyProfiles',
      icon: Globe,
      color: 'cyan',
      description: 'Public company profiles',
      fields: [
        'name',
        'email',
        'phone',
        'location',
        'description',
        'isLaunched',
        'launchDate',
        'profileLink',
        'website',
        'photos',
        'videos',
        'clients',
        'projects',
      ],
    },
    {
      name: 'documentBank',
      icon: Database,
      color: 'slate',
      description: 'Document storage and management',
      fields: [
        'category',
        'fileName',
        'fileSize',
        'fileType',
        'notes',
        'scope',
        'ownerId',
        'ownerEmail',
        'uploadedByRole',
        'storagePath',
        'url',
        'uploadedAt',
        'visibility',
        'versionGroupKey',
      ],
    },
    {
      name: 'entrepreneurProfiles',
      icon: Star,
      color: 'yellow',
      description: 'Entrepreneur assessment profiles',
      fields: [
        'userId',
        'userInfo',
        'businessModel',
        'businessStage',
        'industryContext',
        'marketPosition',
        'operationalMaturity',
        'scalabilityReadiness',
        'capabilities',
        'financialSkills',
        'leadershipSkills',
        'marketingSkills',
        'operationalSkills',
        'technicalSkills',
        'mindset',
        'motivation',
        'problemSolving',
        'selfEfficacy',
        'personality',
        'leadershipStyle',
        'personalityTraits',
        'riskTolerance',
        'lastUpdated',
        'overallScore',
        'notes',
      ],
    },
    {
      name: 'legal_consents',
      icon: Lock,
      color: 'gray',
      description: 'Legal consent records',
      fields: [
        'cookiesAccepted',
        'privacyAccepted',
        'tosAccepted',
        'acceptedAt',
        'createdAt',
        'updatedAt',
      ],
    },
    {
      name: 'moderator',
      icon: Shield,
      color: 'red',
      description: 'Moderator accounts',
      fields: ['email', 'firstname', 'lastname', 'role', 'status'],
    },
    {
      name: 'moderatorReports',
      icon: FileText,
      color: 'blue',
      description: 'Moderator reports',
      fields: [
        'email',
        'title',
        'content',
        'status',
        'attachments',
        'fileData',
        'uploadedBy',
        'createdAt',
        'userType',
      ],
    },
    {
      name: 'organizations',
      icon: Building2,
      color: 'emerald',
      description: 'Organization management',
      fields: ['organization', 'allowAnyDomain', 'allowedDomains', 'members'],
    },
    {
      name: 'otps',
      icon: Clock,
      color: 'amber',
      description: 'One-time passwords',
      fields: ['email', 'otp', 'expiresAt', 'used'],
    },
    {
      name: 'private_chats',
      icon: MessageCircle,
      color: 'pink',
      description: 'Private chat messages',
      fields: ['participants', 'messages', 'createdAt', 'updatedAt'],
    },
    {
      name: 'programRatings',
      icon: Star,
      color: 'yellow',
      description: 'Program ratings and reviews',
      fields: ['programId', 'userId', 'rating', 'review', 'createdAt'],
    },
    {
      name: 'programs',
      icon: BookOpen,
      color: 'violet',
      description: 'Training programs',
      fields: ['title', 'description', 'duration', 'instructor', 'status', 'createdAt'],
    },
    {
      name: 'scalingModulesProgress',
      icon: TrendingUp,
      color: 'green',
      description: 'Scaling module progress',
      fields: ['userId', 'moduleId', 'progress', 'completedAt', 'score'],
    },
    {
      name: 'teamMembers',
      icon: Users,
      color: 'indigo',
      description: 'Team member management',
      fields: ['email', 'firstname', 'lastname', 'role', 'teamId', 'permissions', 'status'],
    },
    {
      name: 'threads',
      icon: MessageSquare,
      color: 'blue',
      description: 'Discussion threads',
      fields: ['title', 'content', 'author', 'category', 'replies', 'createdAt'],
    },
    {
      name: 'userActivity',
      icon: Activity,
      color: 'orange',
      description: 'User activity logs',
      fields: ['userId', 'action', 'timestamp', 'details', 'ipAddress'],
    },
    {
      name: 'users',
      icon: User,
      color: 'gray',
      description: 'General user accounts',
      fields: ['email', 'firstname', 'lastname', 'role', 'status', 'createdAt', 'updatedAt'],
    },
    {
      name: 'weeklyReports',
      icon: Calendar,
      color: 'teal',
      description: 'Weekly progress reports',
      fields: ['userId', 'week', 'content', 'goals', 'achievements', 'challenges', 'createdAt'],
    },
  ];

  const pageSize = 20;

  const getCollectionIcon = collectionName => {
    const collection = collections.find(c => c.name === collectionName);
    return collection ? collection.icon : Database;
  };

  const getCollectionColor = collectionName => {
    const collection = collections.find(c => c.name === collectionName);
    return collection ? collection.color : 'gray';
  };

  const getCollectionDescription = collectionName => {
    const collection = collections.find(c => c.name === collectionName);
    return collection ? collection.description : 'Unknown collection';
  };

  const formatFieldValue = (value, fieldName) => {
    if (value === null || value === undefined || value === '') return '-';

    if (typeof value === 'boolean') return value ? 'Yes' : 'No';

    let parsedValue = value;
    if (typeof value === 'string' && value.trim().startsWith('{')) {
      try {
        parsedValue = JSON.parse(value);
      } catch (e) {}
    }

    if (
      typeof parsedValue === 'object' &&
      parsedValue !== null &&
      parsedValue.seconds !== undefined &&
      parsedValue.nanoseconds !== undefined
    ) {
      try {
        const date = new Date(parsedValue.seconds * 1000 + parsedValue.nanoseconds / 1000000);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (error) {
        return 'Invalid Date';
      }
    }

    if (Array.isArray(parsedValue)) {
      if (parsedValue.length === 0) return 'None';
      if (parsedValue.length <= 3) {
        return parsedValue
          .map(item => (typeof item === 'object' ? '[Object]' : String(item)))
          .join(', ');
      }
      return `${parsedValue.length} items: ${parsedValue
        .slice(0, 3)
        .map(item => (typeof item === 'object' ? '[Object]' : String(item)))
        .join(', ')}...`;
    }

    if (typeof parsedValue === 'object' && parsedValue !== null && !Array.isArray(parsedValue)) {
      const keys = Object.keys(parsedValue);
      if (keys.length <= 5) {
        return keys.map(key => `${key}: ${formatFieldValue(parsedValue[key], key)}`).join(', ');
      }
      return `${keys.length} properties: ${keys.slice(0, 3).join(', ')}...`;
    }

    if (fieldName.includes('At') || fieldName.includes('Date')) {
      try {
        const date = new Date(parsedValue);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        }
      } catch (error) {}
    }

    if (
      fieldName.includes('url') ||
      fieldName.includes('link') ||
      fieldName.includes('image') ||
      fieldName.includes('photo')
    ) {
      if (
        typeof parsedValue === 'string' &&
        (parsedValue.startsWith('http://') || parsedValue.startsWith('https://'))
      ) {
        return parsedValue;
      }
    }

    if (typeof parsedValue === 'string') {
      if (parsedValue.length > 100) {
        return parsedValue.substring(0, 100) + '...';
      }
    }

    return String(parsedValue);
  };

  const getFieldType = (fieldName, value) => {
    if (fieldName.includes('email')) return 'email';
    if (fieldName.includes('phone')) return 'tel';
    if (fieldName.includes('url') || fieldName.includes('link')) return 'url';
    if (fieldName.includes('password')) return 'password';
    if (fieldName === 'businessStage' || fieldName === 'stage') return 'businessStage';
    if (fieldName.includes('status')) return 'status';
    if (fieldName.includes('role')) return 'role';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'checkbox';
    return 'text';
  };

  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getFieldOrder = fieldName => {
    const order = {
      firstname: 1,
      lastname: 2,
      surname: 3,
      name: 4,

      email: 10,
      phone: 11,

      organization: 20,
      company: 21,

      businessStage: 30,
      stage: 31,
      role: 32,
      status: 33,

      createdAt: 90,
      updatedAt: 91,
      created: 92,
      updated: 93,
    };

    return order[fieldName] || 999;
  };

  const searchAllCollections = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const allResults = [];

      const collectionsToSearch =
        selectedCollections.size > 0
          ? Array.from(selectedCollections)
          : collections.map(c => c.name);

      for (const collectionName of collectionsToSearch) {
        try {
          const results = await searchInCollection(collectionName, searchQuery, searchType);
          allResults.push(...results);
        } catch (error) {
          toast.error('Failed to search in ' + collectionName + ': ' + error.message);
        }
      }

      toast.success('Search results: ' + allResults.length);
      setSearchResults(allResults);
      setTotalPages(Math.ceil(allResults.length / pageSize));
    } catch (error) {
      toast.error('Failed to search collections: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchType, selectedCollections]);

  const searchInCollection = async (collectionName, query, searchType) => {
    const collectionRef = collection(firestore, collectionName);
    let queryConstraints = [];

    if (searchType === 'email') {
      queryConstraints.push(where('email', '>=', query));
      queryConstraints.push(where('email', '<=', query + '\uf8ff'));
    } else if (searchType === 'company') {
      queryConstraints.push(where('organization', '>=', query));
      queryConstraints.push(where('organization', '<=', query + '\uf8ff'));
    }

    queryConstraints.push(orderBy(searchType === 'email' ? 'email' : 'organization', 'asc'));
    queryConstraints.push(limit(50));

    const q = firestoreQuery(collectionRef, ...queryConstraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      collection: collectionName,
      ...doc.data(),
    }));
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        searchAllCollections();
      } else {
        setSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType, selectedCollections]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleItemSelection = (itemId, isSelected) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === searchResults.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(searchResults.map(item => item.id)));
    }
  };

  const handleCollectionToggle = collectionName => {
    setSelectedCollections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collectionName)) {
        newSet.delete(collectionName);
      } else {
        newSet.add(collectionName);
      }
      return newSet;
    });
  };

  const handleEditItem = item => {
    setEditingItem(item);
    setEditFormData(item);
    setIsEditing(true);
    setShowEditModal(true);
  };

  const handleFieldChange = (fieldName, value) => {
    setEditFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSaveChanges = async () => {
    if (!editingItem) return;

    setLoading(true);
    try {
      const docRef = doc(firestore, editingItem.collection, editingItem.id);
      await updateDoc(docRef, {
        ...editFormData,
        updatedAt: serverTimestamp(),
      });
      toast.success('Item updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      setIsEditing(false);
      setEditFormData({});
      searchAllCollections();
    } catch (error) {
      toast.error('Failed to update item: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = item => {
    setSelectedItemDetails(item);
    setShowDetails(true);
  };

  const handleToggleExpanded = itemId => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleDeleteClick = item => {
    setDeleteConfirm({ isOpen: true, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.item) return;

    setIsDeleting(true);
    try {
      const docRef = doc(firestore, deleteConfirm.item.collection, deleteConfirm.item.id);
      await deleteDoc(docRef);
      toast.success('Item deleted successfully');
      setDeleteConfirm({ isOpen: false, item: null });
      searchAllCollections();
    } catch (error) {
      toast.error('Failed to delete item: ' + error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, item: null });
  };

  const handleUpdateItem = async (itemId, collectionName, updates) => {
    setLoading(true);
    try {
      const docRef = doc(firestore, collectionName, itemId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      toast.success('Item updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      searchAllCollections();
    } catch (error) {
      toast.error('Failed to update item: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedItems.size === 0) {
      toast.error('Please select items to update');
      return;
    }

    if (!newStage) {
      toast.error('Please select a new stage');
      return;
    }

    if (!updateReason.trim()) {
      toast.error('Please provide a reason for the update');
      return;
    }

    setLoading(true);
    try {
      const selectedItemIds = Array.from(selectedItems);
      let successCount = 0;
      let errorCount = 0;

      for (const itemId of selectedItemIds) {
        try {
          const result = await updateUserBusinessStage('clients', itemId, newStage, updateReason);
          if (result.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          toast.error('Failed to update item ' + itemId + ': ' + error.message);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully updated ${successCount} items`);
        setSelectedItems(new Set());
        setNewStage('');
        setUpdateReason('');
        setShowBulkUpdate(false);
        searchAllCollections();
      }

      if (errorCount > 0) {
        toast.error(`Failed to update ${errorCount} items`);
      }
    } catch (error) {
      toast.error('Failed to update items: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSingleUpdate = async (itemId, newStage) => {
    setLoading(true);
    try {
      const result = await updateUserBusinessStage(
        'clients',
        itemId,
        newStage,
        'Manual update by admin'
      );

      if (result.success) {
        toast.success('Item stage updated successfully');
        searchAllCollections();
      } else {
        toast.error('Failed to update item stage');
      }
    } catch (error) {
      toast.error('Failed to update item stage: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, itemId, type) => {
    try {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      const maxSize = 5 * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Only JPEG, PNG, and WebP are allowed');
        return;
      }

      if (file.size > maxSize) {
        toast.error('File size too large. Maximum size is 5MB');
        return;
      }

      setLoading(true);
      const result = await uploadUserImage(file, itemId, type);

      if (result.success) {
        toast.success('Image uploaded successfully');
        setShowImageUpload(false);
        setImageUploadItem(null);
        searchAllCollections();
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Failed to upload image: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async userId => {
    try {
      const logs = await getAuditLogs(userId, 'stage');
      setAuditLogs(logs);
    } catch (error) {
      toast.error('Failed to load audit logs: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-7xl p-6"
      >
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-3 shadow-lg">
                <Database className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Universal Database Manager</h1>
                <p className="text-gray-600">
                  Comprehensive search, view, and edit across all Firestore collections
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* View Mode Toggle */}
              <div className="flex rounded-lg border border-gray-200 bg-white p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Table
                </button>
              </div>

              {/* Collection Filter */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <Filter className="h-4 w-4" />
                Collections ({selectedCollections.size || collections.length})
              </button>

              {/* Bulk Actions */}
              {selectedItems.size > 0 && (
                <button
                  onClick={() => setShowBulkUpdate(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                >
                  <Edit className="h-4 w-4" />
                  Bulk Update ({selectedItems.size})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Search Bar */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Search Input */}
            <div className="lg:col-span-2">
              <label className="mb-2 block text-sm font-semibold text-gray-700">Search Query</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search across all collections by email, name, organization, or any field..."
                  className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            {/* Search Type */}
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">Search Type</label>
              <select
                value={searchType}
                onChange={e => setSearchType(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="email">Email Address</option>
                <option value="company">Company/Organization</option>
                <option value="name">Name</option>
                <option value="all">All Fields</option>
              </select>
            </div>
          </div>

          {/* Advanced Filters Toggle */}
          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <Settings className="h-4 w-4" />
              Advanced Filters
              {showAdvancedFilters ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            <div className="text-sm text-gray-500">{searchResults.length} results found</div>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 space-y-4 border-t border-gray-200 pt-4"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
                  <select
                    value={filters.role}
                    onChange={e => handleFilterChange('role', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="alumni">Alumni</option>
                    <option value="client">Client</option>
                    <option value="coach">Coach</option>
                    <option value="moderator">Moderator</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={filters.status}
                    onChange={e => handleFilterChange('status', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Business Stage
                  </label>
                  <select
                    value={filters.businessStage}
                    onChange={e => handleFilterChange('businessStage', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">All Stages</option>
                    {BUSINESS_STAGES.map(stage => (
                      <option key={stage} value={stage}>
                        {stage}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Collection Selection</h3>
              <p className="text-gray-600">
                Choose which collections to include in your search. Each collection has specific
                fields and data types.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {collections.map(collection => {
                const IconComponent = collection.icon;
                const isSelected = selectedCollections.has(collection.name);

                return (
                  <motion.label
                    key={collection.name}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-all ${
                      isSelected
                        ? `border-${collection.color}-500 bg-${collection.color}-50`
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleCollectionToggle(collection.name)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <IconComponent className={`h-5 w-5 text-${collection.color}-600`} />
                        <span className="font-medium capitalize text-gray-900">
                          {collection.name.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                      <p className="mb-2 text-sm text-gray-600">{collection.description}</p>
                      <div className="text-xs text-gray-500">{collection.fields.length} fields</div>
                    </div>
                  </motion.label>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedCollections(new Set(collections.map(c => c.name)))}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Select All
                </button>
                <button
                  onClick={() => setSelectedCollections(new Set())}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <XCircle className="h-4 w-4" />
                  Clear All
                </button>
              </div>

              <div className="text-sm text-gray-500">
                {selectedCollections.size || collections.length} of {collections.length} collections
                selected
              </div>
            </div>
          </motion.div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      selectedItems.size === searchResults.length && searchResults.length > 0
                    }
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({selectedItems.size} selected)
                  </span>
                </label>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-500">{searchResults.length} results</div>
                <div className="h-4 w-px bg-gray-300"></div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className="rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="updatedAt">Last Updated</option>
                    <option value="createdAt">Created Date</option>
                    <option value="email">Email</option>
                    <option value="name">Name</option>
                    <option value="status">Status</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                <span className="text-gray-600">Searching Database...</span>
              </div>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Search className="mb-4 h-12 w-12 text-gray-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900">No results found</h3>
              <p className="max-w-md text-center text-gray-500">
                Try adjusting your search query or check if data exists in the selected collections.
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {searchResults.map(item => {
                const collection = collections.find(c => c.name === item.collection);
                const IconComponent = collection?.icon || Database;
                const color = collection?.color || 'gray';
                const isExpanded = expandedItems.has(item.id);

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md"
                  >
                    <div className="mb-3 flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={e => handleItemSelection(item.id, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <IconComponent className={`h-5 w-5 text-${color}-600`} />
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium bg-${color}-100 text-${color}-800`}
                        >
                          {item.collection}
                        </span>
                      </div>
                      <button
                        onClick={() => handleToggleExpanded(item.id)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </div>

                    <div className="space-y-2">
                      <div className="truncate font-medium text-gray-900">
                        {item.firstname && item.lastname
                          ? `${item.firstname} ${item.lastname}`
                          : item.title || item.name || item.company || 'N/A'}
                      </div>
                      <div className="truncate text-sm text-gray-600">{item.email || '-'}</div>
                      <div className="truncate text-sm text-gray-500">
                        {item.organization || item.company || '-'}
                      </div>
                      {item.status && (
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(item.status)}`}
                        >
                          {item.status}
                        </span>
                      )}
                    </div>

                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4 space-y-2 border-t border-gray-200 pt-4"
                      >
                        <div className="text-xs text-gray-500">
                          ID: {item.id.substring(0, 12)}...
                        </div>
                        <div className="text-xs text-gray-500">
                          Updated:{' '}
                          {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : 'N/A'}
                        </div>
                        {Object.entries(item)
                          .filter(
                            ([key]) =>
                              ![
                                'id',
                                'collection',
                                'firstname',
                                'lastname',
                                'email',
                                'organization',
                                'company',
                                'status',
                                'updatedAt',
                              ].includes(key)
                          )
                          .slice(0, 3)
                          .map(([key, value]) => (
                            <div key={key} className="text-xs">
                              <span className="font-medium text-gray-700">{key}:</span>
                              <span className="ml-1 text-gray-600">
                                {formatFieldValue(value, key)}
                              </span>
                            </div>
                          ))}
                      </motion.div>
                    )}

                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleViewDetails(item)}
                          className="rounded p-1 text-gray-400 hover:text-blue-600"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditItem(item)}
                          className="rounded p-1 text-gray-400 hover:text-green-600"
                          title="Edit Item"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className="rounded p-1 text-gray-400 hover:text-red-600"
                          title="Delete Item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => handleViewDetails(item)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700"
                      >
                        View All
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <input
                        type="checkbox"
                        checked={
                          selectedItems.size === searchResults.length && searchResults.length > 0
                        }
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Collection
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Organization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {searchResults.map(item => {
                    const collection = collections.find(c => c.name === item.collection);
                    const IconComponent = collection?.icon || Database;
                    const color = collection?.color || 'gray';

                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item.id)}
                            onChange={e => handleItemSelection(item.id, e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center gap-2">
                            <IconComponent className={`h-4 w-4 text-${color}-600`} />
                            <span
                              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium bg-${color}-100 text-${color}-800`}
                            >
                              {item.collection}
                            </span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="font-medium text-gray-900">
                            {item.firstname && item.lastname
                              ? `${item.firstname} ${item.lastname}`
                              : item.title || item.name || item.company || 'N/A'}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                          {item.email || '-'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                          {item.organization || item.company || '-'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {item.status && (
                            <span
                              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(item.status)}`}
                            >
                              {item.status}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleViewDetails(item)}
                              className="rounded p-1 text-gray-400 hover:text-blue-600"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditItem(item)}
                              className="rounded p-1 text-gray-400 hover:text-green-600"
                              title="Edit Item"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(item)}
                              className="rounded p-1 text-gray-400 hover:text-red-600"
                              title="Delete Item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-700">
              Showing {searchResults.length} results across{' '}
              {new Set(searchResults.map(r => r.collection)).size} collections
            </div>
            <div className="h-4 w-px bg-gray-300"></div>
            <div className="text-sm text-gray-500">{selectedItems.size} selected</div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={!hasMore}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {showDetails && selectedItemDetails && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-h-[90vh] w-full max-w-4xl rounded-xl bg-white shadow-2xl"
            >
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const collection = collections.find(
                        c => c.name === selectedItemDetails.collection
                      );
                      const IconComponent = collection?.icon || Database;
                      const color = collection?.color || 'gray';
                      return (
                        <>
                          <IconComponent className={`h-6 w-6 text-${color}-600`} />
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {selectedItemDetails.firstname && selectedItemDetails.lastname
                                ? `${selectedItemDetails.firstname} ${selectedItemDetails.lastname}`
                                : selectedItemDetails.title ||
                                  selectedItemDetails.name ||
                                  'Item Details'}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {selectedItemDetails.collection} • ID:{' '}
                              {selectedItemDetails.id.substring(0, 12)}...
                            </p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto p-6">
                {(selectedItemDetails.profilePicture ||
                  selectedItemDetails.bannerPhoto ||
                  selectedItemDetails.organizationLogo) && (
                  <div className="mb-6 flex gap-4">
                    {selectedItemDetails.profilePicture && (
                      <div className="flex flex-col items-center">
                        <div className="mb-2 text-xs font-medium text-gray-600">
                          Profile Picture
                        </div>
                        <img
                          src={selectedItemDetails.profilePicture}
                          alt="Profile"
                          className="h-24 w-24 rounded-full border-2 border-gray-200 object-cover"
                          onError={e => {
                            e.target.src =
                              'https://ui-avatars.com/api/?name=' +
                              encodeURIComponent(selectedItemDetails.firstname || 'User');
                          }}
                        />
                      </div>
                    )}
                    {selectedItemDetails.bannerPhoto && (
                      <div className="flex flex-col items-center">
                        <div className="mb-2 text-xs font-medium text-gray-600">Banner Photo</div>
                        <img
                          src={selectedItemDetails.bannerPhoto}
                          alt="Banner"
                          className="h-24 w-24 rounded-lg border-2 border-gray-200 object-cover"
                          onError={e => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    {selectedItemDetails.organizationLogo && (
                      <div className="flex flex-col items-center">
                        <div className="mb-2 text-xs font-medium text-gray-600">
                          Organization Logo
                        </div>
                        <img
                          src={selectedItemDetails.organizationLogo}
                          alt="Organization"
                          className="h-24 w-24 rounded-lg border-2 border-gray-200 bg-white object-cover p-2"
                          onError={e => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {Object.entries(selectedItemDetails)
                    .filter(
                      ([key]) =>
                        key !== 'id' &&
                        key !== 'collection' &&
                        key !== 'profilePicture' &&
                        key !== 'bannerPhoto' &&
                        key !== 'organizationLogo'
                    )
                    .sort(([keyA], [keyB]) => getFieldOrder(keyA) - getFieldOrder(keyB))
                    .map(([key, value]) => (
                      <div key={key} className="space-y-1">
                        <label className="text-sm font-medium capitalize text-gray-700">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </label>
                        <div className="break-words rounded-lg bg-gray-50 p-3 text-sm text-gray-900">
                          {formatFieldValue(value, key)}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={() => setShowDetails(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    handleEditItem(selectedItemDetails);
                  }}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Edit Item
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {showEditModal && editingItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-h-[90vh] w-full max-w-4xl rounded-xl bg-white shadow-2xl"
            >
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Edit Item</h3>
                    <p className="text-sm text-gray-600">
                      {editingItem.collection} • {editingItem.id.substring(0, 12)}...
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingItem(null);
                      setIsEditing(false);
                      setEditFormData({});
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto p-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {Object.entries(editFormData)
                    .filter(([key]) => key !== 'id' && key !== 'collection')
                    .map(([key, value]) => (
                      <div key={key} className="space-y-2">
                        <label className="text-sm font-medium capitalize text-gray-700">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </label>
                        {getFieldType(key, value) === 'businessStage' ? (
                          <select
                            value={value || ''}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value="">Select Business Stage</option>
                            {BUSINESS_STAGES.map(stage => (
                              <option key={stage} value={stage}>
                                {stage}
                              </option>
                            ))}
                          </select>
                        ) : getFieldType(key, value) === 'status' ? (
                          <select
                            value={value || ''}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value="">Select Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="suspended">Suspended</option>
                          </select>
                        ) : getFieldType(key, value) === 'role' ? (
                          <select
                            value={value || ''}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value="">Select Role</option>
                            <option value="admin">Admin</option>
                            <option value="moderator">Moderator</option>
                            <option value="coach">Coach</option>
                            <option value="client">Client</option>
                            <option value="alumni">Alumni</option>
                          </select>
                        ) : getFieldType(key, value) === 'checkbox' ? (
                          <input
                            type="checkbox"
                            checked={Boolean(value)}
                            onChange={e => handleFieldChange(key, e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        ) : getFieldType(key, value) === 'email' ? (
                          <input
                            type="email"
                            value={value || ''}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                        ) : getFieldType(key, value) === 'tel' ? (
                          <input
                            type="tel"
                            value={value || ''}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                        ) : getFieldType(key, value) === 'url' ? (
                          <input
                            type="url"
                            value={value || ''}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                        ) : getFieldType(key, value) === 'number' ? (
                          <input
                            type="number"
                            value={value || ''}
                            onChange={e => handleFieldChange(key, Number(e.target.value))}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                        ) : typeof value === 'object' && value !== null ? (
                          <textarea
                            value={JSON.stringify(value, null, 2)}
                            onChange={e => {
                              try {
                                const parsed = JSON.parse(e.target.value);
                                handleFieldChange(key, parsed);
                              } catch {
                                handleFieldChange(key, e.target.value);
                              }
                            }}
                            rows={4}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                        ) : (
                          <input
                            type="text"
                            value={value || ''}
                            onChange={e => handleFieldChange(key, e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                        )}
                      </div>
                    ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                    setIsEditing(false);
                    setEditFormData({});
                  }}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Changes
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {showBulkUpdate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Bulk Update</h3>
                <button
                  onClick={() => setShowBulkUpdate(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    New Business Stage
                  </label>
                  <select
                    value={newStage}
                    onChange={e => setNewStage(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select stage</option>
                    {BUSINESS_STAGES.map(stage => (
                      <option key={stage} value={stage}>
                        {stage}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Reason for Update
                  </label>
                  <textarea
                    value={updateReason}
                    onChange={e => setUpdateReason(e.target.value)}
                    placeholder="Enter reason for bulk update..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="rounded-lg bg-yellow-50 p-3">
                  <p className="text-sm text-yellow-800">
                    This will update {selectedItems.size} selected items to "{newStage}" stage.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setShowBulkUpdate(false)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpdate}
                  disabled={loading || !newStage || !updateReason.trim()}
                  className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Update Users'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {showImageUpload && imageUploadItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Upload {imageUploadType === 'profile' ? 'Profile Picture' : 'Banner Image'}
                </h3>
                <button
                  onClick={() => setShowImageUpload(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p>
                    Item:{' '}
                    {imageUploadItem.firstname && imageUploadItem.lastname
                      ? `${imageUploadItem.firstname} ${imageUploadItem.lastname}`
                      : imageUploadItem.title || imageUploadItem.name || 'N/A'}
                  </p>
                  <p>Email: {imageUploadItem.email || 'N/A'}</p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Select Image File
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        handleImageUpload(file, imageUploadItem.id, imageUploadType);
                      }
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Supported formats: JPEG, PNG, WebP. Max size: 5MB
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {showAuditLog && selectedItemForAudit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Audit Log -{' '}
                  {selectedItemForAudit.firstname && selectedItemForAudit.lastname
                    ? `${selectedItemForAudit.firstname} ${selectedItemForAudit.lastname}`
                    : selectedItemForAudit.title || selectedItemForAudit.name || 'Item'}
                </h3>
                <button
                  onClick={() => setShowAuditLog(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {auditLogs.length > 0 ? (
                  <div className="space-y-3">
                    {auditLogs.map((log, index) => (
                      <div key={index} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900">
                            Stage: {log.previousStage} → {log.newStage}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(log.affectedAt).toLocaleString()}
                          </div>
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          Changed by: {log.changedBy.email}
                        </div>
                        {log.reason && (
                          <div className="mt-1 text-sm text-gray-600">Reason: {log.reason}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    No audit logs found for this user.
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Item"
          message={`Are you sure you want to delete this item from ${deleteConfirm.item?.collection || ''}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />
      </motion.div>
    </div>
  );
};

export default ClientStageUpdater;
